import { EventSeriesSortKeys } from '@app/graphql/types-and-hooks';
import { SnakeCasedPropertiesDeep } from 'type-fest';

import { TEMPLATES } from './components/wizard/constants';
import {
  getDefaultEndDate,
  getDefaultStartDate,
  getSelectedTimezone,
} from './routes/experience-details/time-utils';

export const SERIES_DEFAULT_PAGE_SIZE = 20;

export interface WizardStep {
  path: string;
  label: string;
  key: string;
  canSkip?: boolean;
}

export const EXPERIENCE_TYPES = {
  WEBINAR: 'webinar_experience',
  VIRTUAL: 'virtual_experience',
  HYBRID: 'hybrid_experience',
  ONSITE: 'onsite_experience',
} as const;

export const EXPERIENCE_TYPE_TO_VENUE_TYPE = {
  [EXPERIENCE_TYPES.WEBINAR]: 'virtual',
  [EXPERIENCE_TYPES.VIRTUAL]: 'virtual',
  [EXPERIENCE_TYPES.HYBRID]: 'hybrid',
  [EXPERIENCE_TYPES.ONSITE]: 'onsite',
};

export const EXPERIENCE_TYPE_TO_EVENT_TEMPLATE = {
  [EXPERIENCE_TYPES.WEBINAR]: 'lite_meetup',
  [EXPERIENCE_TYPES.VIRTUAL]: 'blank',
  [EXPERIENCE_TYPES.HYBRID]: 'blank',
  [EXPERIENCE_TYPES.ONSITE]: 'onsite',
};

export const EXPERIENCE_TYPE_TO_FORMAT = {
  [EXPERIENCE_TYPES.WEBINAR]: 'webinar',
  [EXPERIENCE_TYPES.VIRTUAL]: 'virtual',
  [EXPERIENCE_TYPES.HYBRID]: 'hybrid',
  [EXPERIENCE_TYPES.ONSITE]: 'onsite',
};

export type VENUE = {
  expo: { selected: boolean };
  networking: { selected: boolean };
  reception: { selected: boolean };
  sessions: { selected: boolean };
  stage: { selected: boolean };
};

export type Address = {
  administrativeArea: string;
  country: string;
  formatted: string;
  lat: number;
  lng: number;
  locality: string;
  name: string;
  postalCode: string;
  streetAddress: string;
};

export type EventSeries = {
  id: string;
  name: string;
  eventsCount: number;
};

export type EventSeriesState = {
  data: EventSeries[];
  first: number;
  query: string;
  sortKey: EventSeriesSortKeys;
  sortReverse: boolean;
  status: 'idle' | 'loading' | 'success' | 'failed';
  isSelectionEnabled: boolean;
  selected: EventSeries | null;
  organizationHasSeries: boolean;
  pagination: {
    hasNextPage: boolean;
    endCursor: string | null;
  };
  error: string | null;
};

export type Organization = {
  id: string | null;
  features: {
    onsiteHybridEvents: boolean;
    maxEventLengthHours: number;
  };
  paymentPlan: {
    planName: string;
  };
};

export type State = {
  formData: {
    basicDetails: {
      name: string;
      timeStart: Date;
      timeEnd: Date;
      eventLocation: {
        name: string;
        address?: Address;
      };
    };
    timezone: string;
    description: string;
    template: (typeof EXPERIENCE_TYPES)[keyof typeof EXPERIENCE_TYPES];
  };
  organization: Organization;
  eventSeries: EventSeriesState;
  venue: VENUE;
  eventCreation: {
    status: 'idle' | 'processing' | 'success' | 'failed';
  };
};

export const defaultState: State = {
  formData: {
    basicDetails: {
      name: '',
      timeStart: getDefaultStartDate(),
      timeEnd: getDefaultEndDate(),
      // TODO(Volodymyr): move this out of basicDetails
      eventLocation: {
        name: '',
        address: undefined,
      },
    },
    timezone: getSelectedTimezone().value,
    description: '<p></p>', // backend doesn't allow empty description
    template: EXPERIENCE_TYPES.WEBINAR,
  },
  organization: {
    id: null,
    features: {
      onsiteHybridEvents: true,
      maxEventLengthHours: 240,
    },
    paymentPlan: {
      planName: '',
    },
  },
  eventSeries: {
    data: [],
    first: SERIES_DEFAULT_PAGE_SIZE,
    query: '',
    sortKey: 'NAME' as EventSeriesSortKeys,
    sortReverse: false,
    status: 'idle',
    isSelectionEnabled: false,
    selected: null,
    organizationHasSeries: false,
    pagination: {
      hasNextPage: false,
      endCursor: null,
    },
    error: null,
  },
  venue: TEMPLATES[EXPERIENCE_TYPES.WEBINAR].defaultVenue,
  eventCreation: {
    status: 'idle',
  },
};

export type EventCreationPayload = {
  org_external_id: string;
  /**
   * legacy required field by the backend
   * "blank" is the default value
   */
  event_template: 'blank';
  event_base_setup: {
    name: string;
    time_start: string;
    time_end: string;
    timezone: string;
    event_type: 'private_event' | 'public_event' | 'hidden_event';
  };
  welcome_message: string;
  event_access: 'free' | 'paid' | 'both';
  event_parts: {
    reception: boolean;
    stage: boolean;
    sessions: boolean;
    networking: boolean;
    expo: boolean;
    replay: boolean;
  };
  event_add_ons: {
    sponsors: boolean;
    speakers: boolean;
    q_and_a: boolean;
    polls: boolean;
    chat: boolean;
  };
  event_series?: string[];

  venue_type: 'virtual' | 'onsite' | 'hybrid';
  onboarding_flow: boolean;
  address?: SnakeCasedPropertiesDeep<Address>;
  location_name?: string;
  space_external_id?: string;
};

export type EventCreationResponse = {
  event_external_id: string;
  event_slug: string;
  failed_sections: string[];
  published: boolean;
};
